import { changePassword as updataPassword } from "@/services/global";
import * as storage from "@/utils/storage";
import { exitTenantContext } from "@/services/global";
import {
  LogoutOutlined,
  RollbackOutlined,
  SettingOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { history, useIntl, useModel } from "@umijs/max";
import type { MenuProps } from "antd";
import { message, Spin } from "antd";
import React, { useRef } from "react";
import { flushSync } from "react-dom";
import { normalizeUserAvatar } from "@/utils/defaultAvatar";
import HeaderDropdown from "../HeaderDropdown";
import ChangePassword from "./ChangePassword";
import useStyles from "./index.style";
import {
  clearAuthContext,
  exitTenantSwitch,
  getTenantSwitchContext,
  isTenantSwitching,
} from "@/utils/tokenContext";

export type GlobalHeaderRightProps = {
  menu?: boolean;
  children?: React.ReactNode;
};

type ChangePasswordRef = {
  show: () => void;
  hide: () => void;
};

export const AvatarName = () => {
  const { initialState } = useModel("@@initialState");
  const currentUser = normalizeUserAvatar(initialState?.currentUser);
  const { styles } = useStyles();

  return (
    <span className={`${styles.name} anticon`}>
      {currentUser?.nickname || currentUser?.name || currentUser?.userName || "-"}
    </span>
  );
};

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({ menu, children }) => {
  const changeRef = useRef<ChangePasswordRef | null>(null);
  const intl = useIntl();
  const { styles } = useStyles();
  const { initialState, setInitialState } = useModel("@@initialState");

  const loginOut = async () => {
    if (window.location.pathname !== "/user/login") {
      history.replace({ pathname: "/user/login" });
    }
  };

  const logout = () => {
    flushSync(() => {
      storage.clear();
      clearAuthContext();
      setInitialState((s) => ({ ...s, currentUser: undefined }));
      loginOut();
    });
  };

  const onMenuClick: MenuProps["onClick"] = (event) => {
    const { key } = event;

    if (key === "back-admin") {
      void (async () => {
        try {
          const currentUser = (initialState?.currentUser || {}) as Record<string, unknown>;
          const userId = (currentUser.userId ?? currentUser.id) as string | number | undefined;
          const response = await exitTenantContext(userId);

          if (response.code !== "00000") {
            message.error(response.message || "Failed to switch back");
            return;
          }

          const context = exitTenantSwitch();
          setInitialState((s) => ({
            ...s,
            currentUser: {
              ...(s?.currentUser || {}),
              tenantProjectId: undefined,
              tenantProjectNo: undefined,
              tenantProjectName: undefined,
              tenantSwitchTime: Date.now(),
            },
          }));
          message.success(
            context?.projectName
              ? `返回管理端 (${context.projectName})`
              : "返回管理端",
          );
          history.replace("/operation/project-manage");
        } catch (error) {
          message.error((error as Error).message || "Failed to switch back");
        }
      })();
      return;
    }

    if (key === "logout") {
      logout();
      return;
    }

    if (key === "settings") {
      changeRef.current?.show();
    }
  };

  const loading = (
    <span className={styles.action1}>
      <Spin size="small" style={{ marginLeft: 8, marginRight: 8 }} />
    </span>
  );

  if (!initialState) {
    return loading;
  }

  const { currentUser } = initialState;
  if (!currentUser || (!currentUser.name && !currentUser.nickname && !currentUser.userName)) {
    return loading;
  }

  const tenantSwitching = isTenantSwitching();
  const tenantContext = getTenantSwitchContext();

  const menuItems: MenuProps["items"] = tenantSwitching
    ? [
        {
          key: "back-admin",
          icon: <RollbackOutlined />,
          label: tenantContext?.projectName ? `返回（${tenantContext.projectName}）` : "返回",
        },
      ]
    : [
        ...(menu
          ? [
              {
                key: "center",
                icon: <UserOutlined />,
                label: intl.formatMessage({
                  id: "component.avatarDropdown.personalCenter",
                }),
              },
              {
                key: "settings",
                icon: <SettingOutlined />,
                label: intl.formatMessage({
                  id: "component.avatarDropdown.personalSettings",
                }),
              },
              {
                type: "divider" as const,
              },
            ]
          : []),
        {
          key: "settings",
          icon: <SettingOutlined />,
          label: intl.formatMessage({
            id: "all.table.titleforSys4",
            defaultMessage: "修改密码",
          }),
        },
        {
          type: "divider" as const,
        },
        {
          key: "logout",
          icon: <LogoutOutlined />,
          label: intl.formatMessage({ id: "component.avatarDropdown.logout" }),
        },
      ];

  return (
    <>
      <ChangePassword ref={changeRef} request={updataPassword} loginOut={logout} />
      <HeaderDropdown
        menu={{
          selectedKeys: [],
          onClick: onMenuClick,
          items: menuItems,
        }}
      >
        {children}
      </HeaderDropdown>
    </>
  );
};
